import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, Sparkles, DollarSign } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const BackOfficeCompetitorAnalysisSlide = () => {
  const analysisData = [
    {
      category: "Total Monthly Cost",
      eAssist: "3.5% + $1,775",
      wisdom: "3.5% + $2,494",
      limitations: "Significantly more expensive. Practices typically overpay by ~$2,000/mo due to layered fees.",
      isTotal: true
    },
    {
      category: "Insurance Billing",
      eAssist: "3.5% of Collections",
      wisdom: "3.5% of Collections",
      limitations: "Relies on manual labor which is slower, error-prone, and lacks real-time transparency.",
      isPrice: true
    },
    {
      category: "AI RCM Suite",
      eAssist: "Not Included",
      wisdom: "Not Included",
      limitations: "Zero automation. You pay premium rates for humans to do work that AI could do instantly.",
      isPositive: true
    },
    {
      category: "Insurance Verification",
      eAssist: "$650+/mo",
      wisdom: "$99/mo + $8.95/patient",
      limitations: "Charged as an extra fee. Manual verification is slow and often incomplete compared to AI.",
      isNegative: true
    },
    {
      category: "Patient Billing",
      eAssist: "$925+/mo",
      wisdom: "$1,097–$1,797/mo",
      limitations: "Charged as an extra fee. Manual follow-ups are inconsistent and less effective at collecting.",
      isNegative: true
    },
    {
      category: "Payment Reconciliation",
      eAssist: "$199+/mo",
      wisdom: "Not Included",
      limitations: "Charged as an extra fee or not offered. Manual posting lags behind, making reporting inaccurate.",
      isNegative: true
    },
    {
      category: "Denial Management",
      eAssist: "Often Not Included",
      wisdom: "Included in billing",
      limitations: "Many vendors charge extra for appeals or provide limited follow-through.",
      isNegative: true
    }
  ];

  const renderCell = (value, item) => {
    if (item.isTotal) {
      return <span className="font-extrabold text-slate-900 tracking-tight">{value}</span>;
    }
    if (item.isPrice) {
      return <span className="text-sm font-bold text-slate-700 tracking-tight">{value}</span>;
    }
    if (value === "Included" || value === "Included in billing") {
      return (
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">{value}</span>
        </div>
      );
    }
    if (value === "Not Included") {
      return (
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
            <X className="w-3.5 h-3.5 text-red-400" />
          </div>
          <span className="text-sm text-slate-400">{value}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
          <X className="w-3.5 h-3.5 text-red-400" />
        </div>
        <span className="text-sm text-slate-500">{value}</span>
      </div>
    );
  };

  return (
    <div className="h-full w-full bg-slate-50 relative flex flex-col p-6 lg:p-10 font-sans overflow-hidden">
      
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[120px] opacity-40 -translate-y-1/2 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-red-50 rounded-full blur-[100px] opacity-40 translate-y-1/2 -translate-x-1/4 pointer-events-none" />

      {/* Header Section */}
      <div className="mb-5 flex items-start justify-between gap-6 relative z-10 flex-shrink-0">
        <div className="flex-1">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-3 tracking-tight">
            <span className="text-blue-600">Back Office:</span> What do other RCM companies charge?
          </h2>

          {/* TLDR Callout */}
          <div className="bg-blue-600 rounded-2xl p-5 text-white shadow-md border border-blue-500">
              <p className="text-base font-medium leading-relaxed text-left max-w-full">
                  <span className="font-bold underline decoration-yellow-400 underline-offset-4 mr-2">TL;DR:</span> 
                  Vendors like <span className="font-bold text-blue-100">eAssist, Wisdom, Dental Claim Support, and EZ Dental Billing</span> charge <span className="font-bold text-yellow-300">3.5%</span> just for insurance claim submission. If you try to add patient billing, insurance verification, denial management, or payment reconciliation, the cost bloats up to <span className="font-bold text-yellow-300 text-lg">3.5% + $1,775–$2,494/mo</span>.
              </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg border border-red-100 p-5 flex flex-col items-start gap-1 min-w-[260px] flex-shrink-0"
        >
          <div className="flex items-center gap-2 text-slate-500 font-bold text-xs uppercase tracking-wider mb-1">
            <div className="p-1.5 bg-red-100 rounded-lg">
              <DollarSign className="w-3.5 h-3.5 text-red-600" />
            </div>
            Competitor Cost
          </div>
          <div className="text-3xl font-extrabold text-red-500">
            $1,775 - $2,494
          </div>
          <div className="text-sm font-medium text-slate-400">
            per month + 3.5%
          </div>
        </motion.div>
      </div>

      {/* Main Comparison Table Card */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="flex-1 w-full max-w-[1400px] mx-auto bg-white rounded-3xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] border border-slate-200/60 overflow-hidden flex flex-col relative z-10"
      >
        <div className="flex-1 overflow-auto custom-scrollbar">
          <Table className="h-full">
            <TableHeader className="bg-slate-50 sticky top-0 z-20 shadow-sm border-b border-slate-200">
              <TableRow className="border-0 hover:bg-slate-50">
                <TableHead className="w-[22%] text-slate-400 font-bold text-xs uppercase tracking-widest py-4 pl-8">Comparison Category</TableHead>
                <TableHead className="w-[20%] text-slate-800 font-bold text-sm py-4 bg-red-50/30">
                    eAssist / DCS
                </TableHead>
                <TableHead className="w-[20%] text-slate-800 font-bold text-sm py-4 bg-red-50/30">
                    Wisdom
                </TableHead>
                <TableHead className="w-[38%] text-slate-400 font-bold text-xs uppercase tracking-widest py-4 text-left pr-8">Key Limitations</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analysisData.map((item, idx) => (
                <TableRow 
                    key={idx} 
                    className={`
                        transition-colors border-b border-slate-50 last:border-0 group
                        ${item.isTotal ? 'bg-gradient-to-r from-transparent via-blue-50/30 to-transparent shadow-sm z-10 relative' : 'hover:bg-slate-50/50'}
                    `}
                >
                  <TableCell className={`pl-8 align-middle group-hover:text-slate-900 transition-colors ${item.isTotal ? 'text-slate-900 text-base font-extrabold tracking-wide py-4' : 'font-bold text-slate-700 text-sm py-3.5'}`}>
                    {item.category}
                  </TableCell>
                  
                  {/* eAssist Column */}
                  <TableCell className={`align-middle font-medium bg-red-50/10 group-hover:bg-red-50/20 transition-colors ${item.isTotal ? 'text-base font-extrabold text-slate-900 py-4' : 'text-sm text-slate-600 py-3.5'}`}>
                    {renderCell(item.eAssist, item)}
                  </TableCell>

                  {/* Wisdom Column */}
                  <TableCell className={`align-middle font-medium bg-red-50/10 group-hover:bg-red-50/20 transition-colors ${item.isTotal ? 'text-base font-extrabold text-slate-900 py-4' : 'text-sm text-slate-600 py-3.5'}`}>
                    {renderCell(item.wisdom, item)}
                  </TableCell>

                  {/* Limitations Column */}
                  <TableCell className={`text-left pr-8 align-middle text-slate-500 text-xs leading-snug font-medium ${item.isTotal ? 'py-4' : 'py-3.5'}`}>
                    {item.limitations}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </motion.div>
    </div>
  );
};

export default BackOfficeCompetitorAnalysisSlide;
